/**
 * Changes to rules in force that are waiting for a second person (R44.4, ADR-0014).
 *
 * Nothing here has been applied. The chain on the rest of this page is the chain that was in
 * force, which is the whole point: a change that is "pending" but already showing in the answers
 * is not pending at all.
 *
 * The panel is absent when nothing is waiting, so a deployment with four-eyes off never sees it
 * and nothing in the client has to know whether the setting is on.
 */

import { useState } from 'react';
import { SectionCard } from '~/ui/Surface';
import { Button } from '~/ui/Button';
import { usePendingChanges } from '~/api/queries';
import { useDecidePendingChange } from '~/api/mutations';
import { useToast } from '~/hooks/useToast';
import type * as T from '~/api/schema';
import styles from './Matter.module.css';

export function PendingChanges({ code, matter }: { code: string; matter: string }) {
  const pending = usePendingChanges(code, matter);
  const rows = pending.data?.pending ?? [];

  if (rows.length === 0) return null;

  return (
    <SectionCard
      title={`${rows.length === 1 ? 'One change' : `${rows.length} changes`} waiting for a second person`}
      note="A change to a rule in force is held until somebody other than its author approves it. None of it has been applied — the chain above is the chain in force."
      maxWidth={1034}
    >
      <div className={styles.ruleList}>
        {rows.map((row) => (
          <PendingRow key={row.id} code={code} matter={matter} row={row} />
        ))}
      </div>
    </SectionCard>
  );
}

function PendingRow({
  code,
  matter,
  row,
}: {
  code: string;
  matter: string;
  row: T.PendingChange;
}) {
  const decide = useDecidePendingChange(code, matter);
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const act = (decision: 'approve' | 'reject') => {
    const said = reason.trim();
    decide.mutate(
      { id: row.id, decision, ...(said ? { reason: said } : {}) },
      {
        onSuccess: () => {
          toast.show(
            'ok',
            decision === 'approve'
              ? `${row.lane} updated.`
              : `${row.lane} left as it was, and the reason recorded.`,
          );
          setRejecting(false);
          setReason('');
        },
      },
    );
  };

  return (
    <div className={styles.pendingRow}>
      <div>
        <strong>{row.lane}</strong>
        <p className={styles.pendingMeta}>
          {row.intent === 'delete' ? 'Removal' : 'Change'} submitted by {row.submittedBy}
          {row.reason ? ` — “${row.reason}”` : ''}
        </p>
      </div>

      {!row.mayApprove ? (
        <p className={styles.pendingMeta}>
          You submitted this one. A colleague has to be the second pair of eyes.
        </p>
      ) : rejecting ? (
        <div className={styles.pendingDecide}>
          <label htmlFor={`reject-${row.id}`}>Why are you refusing it?</label>
          <textarea
            id={`reject-${row.id}`}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            rows={2}
          />
          <div>
            <Button
              onClick={() => act('reject')}
              inactive={!reason.trim()}
              pending={decide.isPending}
              variant="danger"
            >
              Record the refusal
            </Button>{' '}
            <Button variant="ghost" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.pendingDecide}>
          <Button onClick={() => act('approve')} pending={decide.isPending}>
            Approve and apply
          </Button>{' '}
          <Button variant="ghost" onClick={() => setRejecting(true)}>
            Refuse
          </Button>
        </div>
      )}
    </div>
  );
}
